import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { formatDate, safeBase64Decode, useTableState } from "./utils";

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_SECRET_KEY = process.env.REACT_APP_API_SECRET_KEY;

const SKIP_KEYS = [
  "email_token",
  "email_verification",
  "wa_token",
  "wa_verification",
  "different_wa_number",
  "plan_id",
  "payment_image",
  "delete_status",
  "approved_by",
  "rejected_by",
  "industry_id",
  "email_notification_status",
  "whatsapp_notification_status",
  "is_private_profile",
  "business_identifier_id",
  "profile_image",
  "log",
  "refund_status",
  "payout_status",
  "payment_status",
  "action_by",
  "otp_verification_status",
  "otp_code",
  "tenant_id",
  "entity_id",
  "id",
  "sno",
  "uid",
  "temp_member_id",
  "payment_screenshot",
  "screenshot_submitted",
  "refund_file_attachment",
  "admin_approval_status",
  "screenshot_submitted",
  "current_start_date",
  "current_end_date",
  "updated_at",
  "tenant_name",
  "tenant_domain",
];

const DECODE_BASE64_KEYS = [
  "email_address",
  "mobile_number",
  "whatsapp_number",
];

function Invoice_delete() {
  const { tenant_id } = useParams();
  const location = useLocation();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ---------------- MODAL STATE ---------------- */
  const [showDataModal, setShowDataModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);

  /* ---------------- FETCH DATA ---------------- */
  useEffect(() => {
    let url = "";

    // Build URL based on route
    if (location.pathname.startsWith("/invoice-delete-log")) {
      if (tenant_id) {
        url = `${API_BASE_URL}/member/invoice-delete-log/${tenant_id}`;
      } else {
        url = `${API_BASE_URL}/member/invoice-delete-log`;
      }
    }

    if (!url) return;

    setLoading(true);

    // Fetch call with API KEY in HEADERS
    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "api-key": API_SECRET_KEY
      }
    })
      .then((res) => {
        if (!res.ok) throw new Error("Server error or Unauthorized");
        return res.json();
      })
      .then((data) => {
        if (data.status === 1 && data.data) {
          setRows(data.data);
        } else {
          setRows([]);
        }
      })
      .catch((err) => {
        setRows([]);
        console.error("Fetch Error:", err);
      })
      .finally(() => setLoading(false));
  }, [tenant_id, location.pathname]);

  const {
    fromDate, setFromDate,
    toDate, setToDate,
    isSearchApplied,
    currentPage, setCurrentPage,
    sortAsc, setSortAsc,
    pageSize, setPageSize,
    handleSearch, handleCancel,
    sortedRows, paginatedRows,
    totalPages, startEntry, endEntry, pages
  } = useTableState(rows);

  const handleExport = () => {
    if (!sortedRows.length) return;

    const EXPORT_ORDER = [
      "first_name", "last_name", "email_address", "mobile_number", "plan_price",
      "action_type", "action_name",
      "entity", "transaction_id", "invoice_type",
      "subscription_id", "subscription_type", "payment_gateway", "description",
      "total_amount", "coupon_id",
      "discount_type", "discount_amount", "net_amount", "tax_name", "tax_percentage",
      "total_tax", "total_tax_amount", "paid_amount", "invoice_pdf",
      "offline_payment_mode", "offline_payment_reference", "offline_payment_approved_by",
      "added_by", "payout_info", "plan_name",
      "payment_remarks",
      "approval_remarks", "delete_remarks", "deleted_by",
      "receipt_number", "receipt_pdf", "refund_remarks", "refund_file_attachment",
      "refunded_by", "invoice_pdf_url", "created_at",
    ];

    const excelData = sortedRows.map((row, index) => {
      const record = { "S.No": index + 1 };

      // Set explicitly ordered columns
      EXPORT_ORDER.forEach(key => {
        let displayValue = row[key];

        if (key === "created_at") {
          displayValue = displayValue ? formatDate(displayValue) : "";
        } else if (DECODE_BASE64_KEYS.includes(key) && displayValue) {
          displayValue = safeBase64Decode(String(displayValue));
        }

        const isEmpty =
          displayValue === null ||
          displayValue === undefined ||
          String(displayValue).trim() === "" ||
          String(displayValue).trim().toLowerCase() === "null";

        const formattedKey = key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        record[formattedKey] = isEmpty
          ? ""
          : typeof displayValue === "object"
            ? JSON.stringify(displayValue)
            : String(displayValue);
      });

      // Append any remaining non-skipped columns
      Object.entries(row).forEach(([key, value]) => {
        const normalizedKey = key.toLowerCase();
        if (SKIP_KEYS.includes(normalizedKey) || EXPORT_ORDER.includes(normalizedKey)) return;

        let displayValue = value;
        if (DECODE_BASE64_KEYS.includes(normalizedKey) && displayValue) {
          displayValue = safeBase64Decode(String(displayValue));
        }

        const isEmpty =
          displayValue === null ||
          displayValue === undefined ||
          String(displayValue).trim() === "" ||
          String(displayValue).trim().toLowerCase() === "null";

        const formattedKey = key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        record[formattedKey] = isEmpty
          ? ""
          : typeof displayValue === "object"
            ? JSON.stringify(displayValue)
            : String(displayValue);
      });

      return record;
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "InvoiceDeleteLogReport");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "InvoiceDeleteLogReport.xlsx");
  };

  return (
    <div className="container-fluid py-3">
      {/* 🔹 TOP CONTROLS: Alignment Fixed */}
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-2 gap-1 bg-light p-1 rounded shadow-sm">

        {/* Left Side: Page Size */}
        <div className="d-flex align-items-center gap-2">
          <span className="text-secondary small fw-bold">Show</span>
          <select
            className="form-select form-select-sm"
            style={{ width: "80px" }}
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {[10, 15, 20, 25, 50].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <span className="text-secondary small fw-bold">entries</span>
        </div>

        {/* Right Side: Filters */}
        <div className="d-flex flex-wrap align-items-center gap-3">
          <div className="d-flex align-items-center gap-2">
            <label className="small fw-bold text-dark mb-0">From</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              disabled={isSearchApplied}
            />
          </div>

          <div className="d-flex align-items-center gap-2">
            <label className="small fw-bold text-dark mb-0">To</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              disabled={isSearchApplied}
            />
          </div>

          <div className="d-flex gap-2">
            <button
              className={`btn btn-sm px-3 fw-bold ${isSearchApplied ? "btn-danger" : "btn-primary"}`}
              onClick={isSearchApplied ? handleCancel : handleSearch}
            >
              {isSearchApplied ? "Clear" : "Search"}
            </button>
            <button className="btn btn-sm btn-success fw-bold" onClick={handleExport}>
              <i className="fa-solid fa-file-excel me-1"></i> Export
            </button>
          </div>
        </div>
      </div>

      {/* 🔹 TABLE */}
      <div className="card shadow-sm border-0">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0 rounded">
            <thead>
              <tr>
                <th
                  className="ps-3 rounded-start"
                  style={{ backgroundColor: "#e2e8f0" }}
                >
                  S.No
                </th>

                <th style={{ backgroundColor: "#e2e8f0" }}>Member</th>
                <th style={{ backgroundColor: "#e2e8f0" }}>Contact</th>
                <th style={{ backgroundColor: "#e2e8f0" }}>Plan Info</th>
                <th style={{ backgroundColor: "#e2e8f0" }}>Action</th>
                <th style={{ backgroundColor: "#e2e8f0" }}>Description</th>
                <th
                  style={{ backgroundColor: "#e2e8f0", cursor: "pointer" }}
                  onClick={() => setSortAsc(!sortAsc)}
                >
                  Date {sortAsc ? "▲" : "▼"}
                </th>
                <th className="text-center rounded-end" style={{ backgroundColor: "#e2e8f0" }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-4">Loading...</td>
                </tr>
              ) : paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-4 text-muted">No data found</td>
                </tr>
              ) : (
                paginatedRows.map((row, index) => {
                  const emailStr = row.email_address;
                  const mobileStr = row.mobile_number;
                  const whatsappStr = row.whatsapp_number;

                  const email = emailStr ? safeBase64Decode(String(emailStr)) : "";
                  const mobile = mobileStr ? safeBase64Decode(String(mobileStr)) : "";
                  const whatsapp = whatsappStr ? safeBase64Decode(String(whatsappStr)) : "";

                  const planName = row.plan_name || "";
                  const planPrice = row.plan_price || "";

                  const rFirstName = row.first_name || "";
                  const rLastName = row.last_name || "";
                  const memberName = `${rFirstName} ${rLastName}`.trim() || "-";

                  const formattedDateVal = row.created_at ? formatDate(row.created_at) : "";
                  const finalDateStr = formattedDateVal ? formattedDateVal.split(" ").slice(0, 3).join(" ") : "-";

                  return (
                    <tr key={index}>
                      <td className="ps-3">{startEntry + index}</td>
                      <td>
                        <div className="small fw-medium text-capitalize">
                          {memberName}
                        </div>
                      </td>
                      <td>
                        <div className="small">
                          {email && <div><i className="fa-solid fa-envelope text-secondary me-1"></i> {email}</div>}
                          {mobile && <div><i className="fa-solid fa-phone text-secondary me-1"></i> {mobile}</div>}
                          {whatsapp && <div><i className="fa-brands fa-whatsapp text-success me-1"></i> {whatsapp}</div>}
                          {!email && !mobile && !whatsapp && "-"}
                        </div>
                      </td>
                      <td>
                        <div className="small text-nowrap">
                          {planName && <div>{planName}</div>}
                          {planPrice && <div><strong>Price:</strong> {planPrice}</div>}
                          {!planName && !planPrice && "-"}
                        </div>
                      </td>
                      <td>
                        {row.tenant_domain && row.action_by?.member_id ? (
                          <a
                            href={`${row.tenant_domain}/portal/members/member-details/${row.action_by.member_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-decoration-none fw-medium"
                          >
                            {row.action_by.member_name}
                          </a>
                        ) : (
                          row.action_by?.member_name || "-"
                        )}
                      </td>
                      <td>
                        <div>
                          {row.description
                            ? row.description.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                            : "-"}
                        </div>
                      </td>
                      <td className="text-nowrap">{finalDateStr}</td>
                      <td className="text-center">
                        <div className="d-flex gap-2 justify-content-center">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            title="View Data"
                            onClick={() => {
                              setSelectedData(row);
                              setShowDataModal(true);
                            }}
                          >
                            <i className="fa-solid fa-eye"></i>
                          </button>
                          {row.invoice_pdf_url && (
                            <a
                              href={row.invoice_pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-info"
                              title="View Invoice"
                            >
                              <i className="fa-solid fa-file-pdf"></i>
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 🔹 PAGINATION */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mt-4">
        <span className="text-secondary small">
          Showing <strong>{startEntry}</strong> to <strong>{endEntry}</strong> of <strong>{sortedRows.length}</strong> entries
        </span>

        <nav>
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setCurrentPage((p) => p - 1)}>Previous</button>
            </li>
            {pages.map((p) => (
              <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage(p)}>{p}</button>
              </li>
            ))}
            <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setCurrentPage((p) => p + 1)}>Next</button>
            </li>
          </ul>
        </nav>
      </div>

      {/* 🔹 DATA MODAL */}
      {showDataModal && (
        <>
          <div className="modal fade show d-block">
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-dark text-white">
                  <h5 className="modal-title fw-semibold">Delete Log Data</h5>
                  <button className="btn-close btn-close-white" onClick={() => setShowDataModal(false)} />
                </div>
                <div className="modal-body bg-light">
                  <div className="card shadow-sm border-0">
                    <div className="card-body p-0">
                      {(!selectedData || typeof selectedData !== "object" || Object.keys(selectedData).length === 0) ? (
                        <div className="alert alert-secondary text-center mb-0 small m-3">
                          No data available
                        </div>
                      ) : (
                        <table className="table table-sm table-bordered align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: "40%", paddingLeft: "15px" }}>Key</th>
                              <th>Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(selectedData).map(([key, value]) => {
                              const normalizedKey = key.toLowerCase();
                              if (SKIP_KEYS.includes(normalizedKey)) return null;

                              const formattedKey = key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                              let displayValue = value;

                              if (DECODE_BASE64_KEYS.includes(normalizedKey) && displayValue) {
                                displayValue = safeBase64Decode(String(displayValue));
                              }

                              const isEmpty = displayValue === null || displayValue === undefined || String(displayValue).trim() === "" || String(displayValue).trim().toLowerCase() === "null";

                              let renderValue;
                              if (isEmpty) {
                                renderValue = <span className="text-muted fst-italic">empty</span>;
                              } else if (typeof displayValue === "object") {
                                renderValue = <pre className="mb-0 small">{JSON.stringify(displayValue, null, 2)}</pre>;
                              } else {
                                renderValue = String(displayValue);
                              }

                              return (
                                <tr key={key}>
                                  <td className="fw-semibold text-muted" style={{ paddingLeft: "15px" }}>{formattedKey}</td>
                                  <td className="text-dark">
                                    {renderValue}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer bg-light">
                  <button className="btn btn-secondary btn-sm px-4" onClick={() => setShowDataModal(false)}>Close</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
}

export default Invoice_delete;
